import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { API } from 'constants';
import cycleClient from '../../../services/cycle-api';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useFetchDataOnLoad } from '../../../hooks/useFetchDataOnLoad';
import { useOverlayState } from '../../../hooks/useOverlayState';
import { useManageSearchParams } from '../../../hooks/useManageSearchParams';
import { useDefaultSearchParams } from '../../../hooks/useDefaultSearchParams';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import { capitalizeFirstLetter } from '../../../services/strings';

import { CONFIGURE_TAB } from '../../../constants/configure';

import { Tabs } from '../../../design-system';
import CycleTabsContent from './CycleTabsContent/CycleTabsContent';
import ArchiveCycleModal from './ArchiveCycleModal/ArchiveCycleModal';
import CycleMoreOptionsPopup from './CycleMoreOptionsPopup/CycleMoreOptionsPopup';
import ConfigureDetailPageLayout from '../ConfigureDetailPageLayout/ConfigureDetailPageLayout';

export const TAB = { overview: 'overview', stages: 'stages' };

const tabs = [TAB.overview, TAB.stages];
const DEFAULT_TAB = TAB.overview;

const formattedTabs = tabs.map((key) => ({ key, name: capitalizeFirstLetter(key) }));

const CycleDetailPage = () => {
    const { cycleId } = useParams();
    const [searchParams] = useSearchParams();

    const location = useLocation();
    const navigate = useNavigate();

    const activeTab = searchParams.get('tab');
    const activeTabIndex = Math.max(tabs.indexOf(activeTab), 0);

    const backLinkHref = location.state?.from ?? `/configure?tab=${CONFIGURE_TAB.cycles}`;

    const { setSearchParam } = useManageSearchParams();
    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    // cycle.current_stage might be outdated (cycle.current_stage is not in use now on this page)
    const { data: cycleDetail, setData: setCycleDetail } = useFetchDataOnLoad({
        client: cycleClient,
        route: `${API.ROUTES.cycle.cycle}${cycleId}/`,
        onError: (e) =>
            handlePageDataLoadError({
                e,
                redirectPath: `/configure?tab=${CONFIGURE_TAB.cycles}`,
                generalErrorHandler: () => navigate(backLinkHref),
            }),
    });

    useDefaultSearchParams({
        checkPage: false,
        checkSort: false,
        checkTab: true,
        defaultTab: DEFAULT_TAB,
        availableTabs: tabs,
    });

    useDocumentTitle(cycleDetail?.name);

    const {
        isOpened: isArchiveModalOpened,
        open: openArchiveModal,
        close: closeArchiveModal,
    } = useOverlayState();

    const renderMoreOptions = ({ onClose }) => {
        return (
            <CycleMoreOptionsPopup
                id={cycleDetail.id}
                openConfirmArchiveModal={openArchiveModal}
                onClose={onClose}
            />
        );
    };

    return (
        <ConfigureDetailPageLayout
            name={cycleDetail?.name}
            description={cycleDetail?.description}
            truncateDescription
            backLinkHref={backLinkHref}
            breadcrumbs="Configure / Cycle Overview"
            renderMoreOptions={renderMoreOptions}
            isLoading={!cycleDetail}
        >
            <div className="max-w-max">
                <Tabs
                    tabs={formattedTabs}
                    tabIndex={activeTabIndex}
                    onTabChanged={(tab) => setSearchParam('tab', tab.key)}
                />
            </div>

            <CycleTabsContent
                activeTab={activeTab}
                cycleDetail={cycleDetail}
                setCycleDetail={setCycleDetail}
            />

            {isArchiveModalOpened && (
                <ArchiveCycleModal
                    id={cycleDetail.id}
                    name={cycleDetail.name}
                    onDeleteSuccess={() => navigate(backLinkHref, { replace: true })}
                    onClose={closeArchiveModal}
                />
            )}
        </ConfigureDetailPageLayout>
    );
};

export default CycleDetailPage;
